import { useCallback, useEffect, useRef, useState } from 'react';

import { isConflictError } from './conflictError';

/** Where a conflict has got to. */
export type ConflictPhase = 'refreshing' | 'ready' | 'failed';

/** A save that was refused because someone else saved first. */
export interface ConflictState<TInput> {
  /** What the person was trying to save, exactly as they entered it. */
  readonly input: TInput;
  /**
   * `refreshing` while the latest version is being read, `ready` once it is on
   * screen and their changes can be applied again, `failed` when it could not be read.
   */
  readonly phase: ConflictPhase;
}

/** What a screen supplies to recover from a conflict. */
export interface ConflictRecoveryOptions<TInput> {
  /**
   * Reads the record again so the screen shows what the server holds now.
   *
   * A rejection counts as failing to read it, and so does a resolved TanStack
   * Query result with `isError` set, which is what `query.refetch()` gives
   * unless it is told to throw.
   */
  readonly refetch: () => Promise<unknown>;
  /**
   * Saves the person's input again, against the record `refetch` just loaded.
   *
   * Called after the screen has re-rendered with the new version, so a closure
   * over the record (`record.version`) is current by then.
   */
  readonly apply: (input: TInput) => void;
  /** Called when they keep the latest version instead; reset the form to it here. */
  readonly onDiscard?: () => void;
}

/** Whether a resolved refetch result reports that the read itself failed. */
function readFailed(result: unknown): boolean {
  return (
    typeof result === 'object' && result !== null && 'isError' in result && result.isError === true
  );
}

/** What {@link useConflictRecovery} hands back. */
export interface ConflictRecovery<TInput> {
  /** Non-null from the moment a save is refused as a conflict until it is applied or dismissed. */
  readonly conflict: ConflictState<TInput> | null;
  /**
   * Feeds a failed save through the recovery.
   *
   * @returns true when it was a conflict, which is now being handled; false when it was
   *     anything else, which is still the caller's to show
   */
  readonly handleError: (error: unknown, input: TInput) => boolean;
  /** Saves their input again, once the latest version is on screen. */
  readonly applyAgain: () => void;
  /** Reads the latest version again after that failed. */
  readonly retryRefresh: () => void;
  /** Gives up on their input and keeps what the server holds. */
  readonly discard: () => void;
  /** Hides the notice and leaves the form as it is, for when the latest version cannot be had. */
  readonly dismiss: () => void;
}

/**
 * The one way a screen answers a 409.
 *
 * A conflict used to mean four different things depending on the page: a toast,
 * a dead form, a silent refetch, a reload button that threw the edit away. The
 * standard is three steps, in this order. The latest version is read again, so
 * the screen stops lying. The person's input is kept, so nothing they typed is
 * lost. And they are offered "Apply my changes again", which saves it against
 * the new version — a decision they make knowing someone else's change exists,
 * rather than one the screen makes for them.
 *
 * Pair it with `ConflictNotice`, which says all of that in the standard words:
 *
 * ```tsx
 * const save = useMutation({
 *   mutationFn: (draft: Draft) => updateItem(item.id, item.version, draft),
 *   onSuccess: () => { toast.show({ message: 'Saved.', tone: 'success' }); },
 *   onError: (error, draft) => {
 *     if (!recovery.handleError(error, draft)) setFailure(error.message);
 *   },
 * });
 * const recovery = useConflictRecovery<Draft>({
 *   refetch: () => itemQuery.refetch(),
 *   apply: (draft) => { save.mutate(draft); },
 *   onDiscard: () => { setDraft(toDraft(itemQuery.data)); },
 * });
 *
 * return (<>
 *   <ConflictNotice recovery={recovery} subject="This item" />
 *   <ItemForm draft={draft} onChange={setDraft} onSubmit={() => { save.mutate(draft); }} />
 * </>);
 * ```
 *
 * Keep the draft in state the screen owns and do not reset it when the record
 * refetches; `conflict.input` is the copy to restore from if the form must be rebuilt.
 *
 * @param options see {@link ConflictRecoveryOptions}
 * @returns see {@link ConflictRecovery}
 */
export function useConflictRecovery<TInput>(
  options: ConflictRecoveryOptions<TInput>,
): ConflictRecovery<TInput> {
  const [conflict, setConflict] = useState<ConflictState<TInput> | null>(null);
  // The newest callbacks, so an answer that arrives late acts on the screen as it is now.
  const latest = useRef(options);
  useEffect(() => {
    latest.current = options;
  });
  // Which attempt is current; the answer to a superseded one is ignored.
  const attempt = useRef(0);

  const refresh = useCallback((input: TInput) => {
    attempt.current += 1;
    const mine = attempt.current;
    setConflict({ input, phase: 'refreshing' });
    latest.current.refetch().then(
      (result) => {
        if (attempt.current === mine) {
          setConflict({ input, phase: readFailed(result) ? 'failed' : 'ready' });
        }
      },
      () => {
        if (attempt.current === mine) {
          setConflict({ input, phase: 'failed' });
        }
      },
    );
  }, []);

  const handleError = useCallback(
    (error: unknown, input: TInput): boolean => {
      if (!isConflictError(error)) {
        return false;
      }
      refresh(input);
      return true;
    },
    [refresh],
  );

  const applyAgain = useCallback(() => {
    if (conflict?.phase !== 'ready') {
      return;
    }
    attempt.current += 1;
    setConflict(null);
    latest.current.apply(conflict.input);
  }, [conflict]);

  const retryRefresh = useCallback(() => {
    if (conflict !== null) {
      refresh(conflict.input);
    }
  }, [conflict, refresh]);

  const dismiss = useCallback(() => {
    attempt.current += 1;
    setConflict(null);
  }, []);

  const discard = useCallback(() => {
    dismiss();
    latest.current.onDiscard?.();
  }, [dismiss]);

  return { conflict, handleError, applyAgain, retryRefresh, discard, dismiss };
}
